import { EditTwoTone } from "@mui/icons-material";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	IconButton,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import { chronos } from "nhb-toolbox";
import React from "react";
import Label from "src/components/Label";
import { useApiMutation } from "src/hooks/useApiMutation";
import type { IProductCategory } from "src/Types/product.categories.types";
import UpdateCategory from "./UpdateCategory";

export interface Props {
	category: IProductCategory;
}

const CategoryTableRow = ({ category }: Props) => {
	const theme = useTheme();

	const { mutate: deleteCategory, isPending } = useApiMutation<void, void>({
		method: "DELETE",
		endPoint: `/api/fair/product-category/${category.id}`,
		invalidateQueries: ["ProductCategories"],
	});

	const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

	// Update Functions
	const [openUpdate, setOpenUpdate] = React.useState(false);

	const handleClickOpenUpdate = () => {
		setOpenUpdate(true);
	};

	const handleCloseUpdate = () => {
		setOpenUpdate(false);
	};

	// Delete Functions
	const [openDelete, setOpenDelete] = React.useState(false);

	const handleClickOpenDelete = () => {
		setOpenDelete(true);
	};

	const handleCloseDelete = () => {
		setOpenDelete(false);
	};

	const handleDelete = () => {
		deleteCategory();
		handleCloseDelete();
	};

	return (
		<>
			<TableRow hover>
				<TableCell>{category.id}</TableCell>
				<TableCell>{category.name}</TableCell>
				<TableCell>
					{chronos(category.created_at).formatStrict(
						"dd, mmm Do, yy; hh:mm:ss A"
					)}
				</TableCell>
				<TableCell>{category.created_by_name}</TableCell>
				<TableCell>
					<Label color={category.status ? "success" : "warning"}>
						{category.status ? "Active" : "Inactive"}
					</Label>
				</TableCell>
				<TableCell>
					<Label color={category.is_default ? "success" : "warning"}>
						{category.is_default ? "Yes" : "No"}
					</Label>
				</TableCell>
				<TableCell>
					<Tooltip title='Update Category' arrow>
						<IconButton
							onClick={handleClickOpenUpdate}
							sx={{
								"&:hover": {
									background: theme.colors.success.lighter,
								},
								color: theme.palette.success.dark,
							}}
							color='inherit'
							size='small'
						>
							<EditTwoTone fontSize='small' />
							{/* Delete Confirmation modal  */}
						</IconButton>
					</Tooltip>
					<Tooltip title='Delete Category' arrow>
						<IconButton
							onClick={handleClickOpenDelete}
							sx={{
								"&:hover": {
									background: theme.colors.error.lighter,
								},
								color: theme.palette.error.main,
							}}
							color='inherit'
							size='small'
						>
							<DeleteTwoToneIcon fontSize='small' />
							{/* Delete Confirmation modal  */}
						</IconButton>
					</Tooltip>
				</TableCell>
			</TableRow>

			{/* Modal */}
			<Dialog
				fullScreen={fullScreen}
				open={openUpdate}
				maxWidth='md'
				onClose={handleCloseUpdate}
				aria-labelledby='responsive-dialog-title'
			>
				<DialogContent>
					<Box sx={{ mb: 2 }}>
						<Typography sx={{ mb: 2 }}>
							Update Category: {category.name}
						</Typography>
					</Box>
					<UpdateCategory
						category={category}
						handleClose={handleCloseUpdate}
					/>
				</DialogContent>
				<DialogActions>
					<Button autoFocus onClick={handleCloseUpdate}>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default CategoryTableRow;
